import {
  Box,
  Typography,
  Button,
  Chip,
  CircularProgress,
  TextField,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Badge,
} from "@mui/material";
import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { ENTITY_OPTIONS_QUERY_KEY } from "src/hooks/useEntityOptions";
import { useParams, useNavigate } from "react-router-dom";
import { DashboardWrapper } from "src/components/common/DashboardWrapper";
import Snackbar from "src/components/common/Snackbar";
import { getStyles } from "src/styles/theme";
import { SnackbarType } from "src/types/common";
import {
  getGCADetails,
  updateProfileComprehensive,
  getGCAUserUpdateHistory,
} from "src/utils/api/gca";
import { getStates } from "src/utils/api/roles";
import { userStatus, getImageById } from "src/utils/api/dashboard";
import { useAppSelector } from "src/redux/store";
import defaultStyles, { StylesClasses } from "./styles";
import QueriesDialog from "./QueriesPanel";
import { getAdminQueryUnread } from "src/utils/api/adminQuery";

const styles = getStyles<StylesClasses>(defaultStyles);

interface GCAData {
  id: string;
  gcaType: string;
  status: string;
  createdAt: string;
  updatedAt: string;

  // Government Institutions
  nameOfInstitution?: string;
  typeOfInstitution?: string;
  ministry?: string;
  organizationPAN?: string;
  organizationGST?: string;
  institutionalEmail?: string;
  officialContactNumber?: string;
  website?: string;
  address?: string;
  state?: string;
  district?: string;
  pincode?: string;
  repFullName?: string;
  repDesignation?: string;
  repEmail?: string;
  repMobile?: { countryCode: string; number: string };
  repEmpId?: string;

  // PSU
  nameOfPSU?: string;
  ownershipType?: string;
  psuParentCompanyDetails?: string;
  psuSubsidiaryDetails?: string;
  cinNumber?: string;
  gstNumber?: string;
  primaryEmail?: string;
  primaryContactNumber?: string;
  officeAddress?: string;

  // NGO
  nameOfNGO?: string;
  registrationType?: string;
  ngoRegistrationNumber?: string;
  ngoGSTNumber?: string;
  ngoPANNumber?: string;
  ngoDarpanID?: string;
  ngoEmail?: string;
  ngoContactNumber?: string;
  ngoNatureOfWork?: string;

  // Private Company
  companyName?: string;
  companyPAN?: string;
  companyGSTNumber?: string;
  companyEmail?: string;
  companyMobileNumber?: string;
  companyParentDetails?: string;
  companySubsidiariesDetails?: string;
  companyNatureOfBusiness?: string;
  companyStatesOfOperation?: string;
  companyIncorporationDate?: string;

  // Public Limited Company
  registeredCompanyName?: string;
  plcCINNumber?: string;
  plcPANNumber?: string;
  plcGSTNumber?: string;
  sebiListingStatus?: string;
  corporateOfficeEmail?: string;
  plcParentCompanyDetails?: string;
  plcSubsidiariesDetails?: string;

  // Philanthropies/Foundations
  foundationName?: string;
  foundationRegistrationNumber?: string;
  foundationPANNumber?: string;
  foundationEmail?: string;
  foundationMobile?: { countryCode: string; number: string };
  foundationLegalStatus?: string;
  foundationFundingSource?: string;

  // Individuals
  individualFullName?: string;
  individualDateOfBirth?: string;
  individualGender?: string;
  individualEmail?: string;
  individualMobile?: { countryCode: string; number: string };
  individualAadhaarNumber?: string;
  individualPANNumber?: string;
  individualOccupation?: string;
  individualOccupationDetails?: string;

  // Registered Group
  groupName?: string;
  groupType?: string;
  groupEmail?: string;
  groupContactNumber?: { countryCode: string; number: string };
  groupRegistrationNumber?: string;
  groupOfficeAddress?: string;
  groupOfficeState?: string;
  groupOfficeDistrict?: string;
  groupOfficePincode?: string;
  groupRepresentativeFullName?: string;
  groupRepresentativeDesignation?: string;
  groupRepresentativeEmail?: string;
  groupRepresentativePhone?: string;
  groupRepresentativeEmployeeId?: string;

  documents?: any[];
}

// Option constants for dropdowns
const institutionTypeOptions = [
  { value: "Central Govt", label: "Central Government" },
  { value: "State Govt", label: "State Government" },
  { value: "Autonomous Body", label: "Autonomous Body" },
  { value: "Statutory Body", label: "Statutory Body" },
];

const ownershipTypeOptions = [
  { value: "Central PSU", label: "Central PSU" },
  { value: "State PSU", label: "State PSU" },
];

const registrationTypeOptions = [
  { value: "Society", label: "Society" },
  { value: "Trust", label: "Trust" },
  { value: "Section 8 Company", label: "Section 8 Company" },
];

const natureOfWorkOptions = [
  { value: "Environment", label: "Environment" },
  { value: "Social", label: "Social" },
  { value: "Plantation", label: "Plantation" },
  { value: "Livelihood", label: "Livelihood" },
  { value: "Others", label: "Others" },
];

const sebiListingStatusOptions = [
  { value: "NSE", label: "NSE" },
  { value: "BSE", label: "BSE" },
  { value: "Unlisted Public Ltd", label: "Unlisted Public Limited" },
];

const legalStatusFoundationOptions = [
  { value: "Society", label: "Society" },
  { value: "Trust", label: "Trust" },
  { value: "Non-profit Foundation", label: "Non-profit Foundation" },
];

const occupationDetailsOptions = [
  { value: "Government", label: "Government" },
  { value: "Private", label: "Private" },
  { value: "Student", label: "Student" },
  { value: "Self-employed", label: "Self-employed" },
  { value: "Other", label: "Other" },
];

const genderOptions = [
  { value: "Male", label: "Male" },
  { value: "Female", label: "Female" },
  { value: "Others", label: "Others" },
];

const groupTypeOptions = [
  { value: "SHG", label: "SHG" },
  { value: "FPO", label: "FPO" },
  { value: "Village Committee", label: "Village Committee" },
  { value: "Others", label: "Others" },
];

const GCATypeLabels: Record<string, string> = {
  GOVERNMENT_INSTITUTIONS: "Government Institutions",
  PSU: "Public Sector Undertakings (PSUs)",
  NGO: "Non-Governmental Organizations (NGOs)",
  PRIVATE_COMPANY: "Private Companies",
  PUBLIC_LIMITED_COMPANY: "Public Limited Company",
  PHILANTHROPY: "Philanthropies / Foundations",
  INDIVIDUAL: "Individuals",
  REGISTERED_GROUP: "Registered Group of Individuals",
};

// Human-readable labels for the raw document type enums coming from the
// backend. Kept in sync with the user portal so titles render nicely instead
// of overflowing the card with long underscored codes.
const DocumentTypeLabels: Record<string, string> = {
  PAN: "PAN",
  GST_CERTIFICATE: "GST Certificate",
  CIN: "Certificate of Incorporation (CIN)",
  AADHAAR: "Aadhaar",
  CERTIFICATE: "Registration / Authorization Certificate",
  TAX_EXEMPTION_CERTIFICATE: "12A / 80G Certificate",
  DARPAN_ID: "Darpan Certificate",
  AUDITED_FINANCIAL_STATEMENTS: "Audited Financial Statements",
  STOCK_EXCHANGE_LISTING: "Stock Exchange Listing",
  CSR_ESG_CERTIFICATE: "CSR / ESG Document",
  QUALIFICATION_CERTIFICATE: "Qualification Certificate",
  EXPERIENCE_CERTIFICATE: "Experience Certificate",
  PARENT_COMPANY_DETAILS: "Parent Company Details",
  SUBSIDIARIES_DETAILS: "Subsidiaries Details",
};

// Thumbnail that resolves a fresh pre-signed URL by S3 key (the `serialNo`)
// so previews don't break when the original `frontUrl` has expired. Falls
// back to the stored URL if the lookup fails.
const DocumentPreview = ({
  serialNo,
  fallbackUrl,
}: {
  serialNo?: string;
  fallbackUrl?: string;
}) => {
  const [src, setSrc] = useState<string>("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!serialNo) {
        if (!cancelled) setSrc(fallbackUrl || "");
        return;
      }
      try {
        const res: any = await getImageById(serialNo);
        const fresh = res?.data?.fileUrl || res?.data?.data?.fileUrl;
        if (!cancelled) setSrc(fresh || fallbackUrl || "");
      } catch {
        if (!cancelled) setSrc(fallbackUrl || "");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [serialNo, fallbackUrl]);

  const isPdf = (src || fallbackUrl || "").toLowerCase().includes(".pdf");

  if (!src && !fallbackUrl) {
    return (
      <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: 1,
          backgroundColor: "#f0f0f0",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#9e9e9e",
          fontSize: 11,
        }}
      >
        No file
      </Box>
    );
  }

  if (isPdf) {
    return (
      <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: 1,
          backgroundColor: "#fafafa",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#d32f2f",
          fontWeight: 600,
          fontSize: 14,
          border: "1px solid #eee",
        }}
      >
        PDF
      </Box>
    );
  }

  return (
    <Box
      component="img"
      src={src || fallbackUrl}
      alt="document preview"
      onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
        const target = e.target as HTMLImageElement;
        target.src = "/assets/svg/receipt.svg";
      }}
      sx={{
        width: 72,
        height: 72,
        objectFit: "cover",
        borderRadius: 1,
        border: "1px solid #eee",
      }}
    />
  );
};

const GCAView = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const userId = id || "";
  const role = useAppSelector((state: any) => state.root.user)?.data?.role;
  const isApprover = role === "Approver";
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editData, setEditData] = useState<GCAData | null>(null);
  const [queriesOpen, setQueriesOpen] = useState(false);

  const { data: queriesUnreadResp } = useQuery(
    ["adminQueryUnread", userId],
    () => getAdminQueryUnread(userId),
    {
      enabled: !!userId,
      refetchInterval: 30000,
    },
  );
  const queriesUnreadCount = queriesUnreadResp?.data?.data?.count ?? 0;
  const [stateOptions, setStateOptions] = useState<any[]>([]);
  const [showSnackbar, setShowSnackbar] = useState<SnackbarType>({
    severity: "success",
    message: "",
  });
  const [isActive, setIsActive] = useState(false);
  const [openedDocIds, setOpenedDocIds] = useState<Set<string | number>>(
    new Set(),
  );

  // Fetch states from API
  useQuery(["getStates"], () => getStates(), {
    onSuccess: (data) => setStateOptions(data.data),
    refetchOnMount: true,
  });

  // Compute district options based on selected state
  const districtOptions = useMemo(() => {
    if (!editData?.state) return [];
    const selectedState = stateOptions.find(
      (s: any) => s.id === editData.state,
    );
    return selectedState?.districts || [];
  }, [editData?.state, stateOptions]);

  const {
    data: gcaResponse,
    isLoading,
    isError,
    refetch: refetchGCADetails,
  } = useQuery(["gcaDetails", userId], () => getGCADetails(userId), {
    onError: (err: any) => {
      setShowSnackbar({
        severity: "error",
        message: err?.response?.data?.message || "Failed to load GCA details",
      });
    },
    enabled: !!userId,
  });

  const gcaQueryClient = useQueryClient();

  const { mutate: mutateUserStatus, isLoading: isStatusUpdating } = useMutation(
    "changeGCAStatus",
    (data: { status: string; userId: string }) => userStatus(data),
    {
      onSuccess: (_res, variables) => {
        setShowSnackbar({
          severity: "success",
          message:
            variables.status === "ACTIVE"
              ? "User activated."
              : "User deactivated. Review all documents to activate again.",
        });
        if (variables.status === "ACTIVE") {
          // keep checks; do nothing
        } else {
          setOpenedDocIds(new Set());
        }
        refetchGCADetails();
        // activating/deactivating a GCA changes the entity dropdown list
        gcaQueryClient.invalidateQueries(ENTITY_OPTIONS_QUERY_KEY);
      },
      onError: (err: any) => {
        setShowSnackbar({
          severity: "error",
          message:
            err?.response?.data?.message || "Failed to update user status",
        });
      },
    },
  );

  // Fetch user update history
  const { data: updateHistoryResponse, isLoading: isHistoryLoading } = useQuery(
    ["updateHistory", userId],
    () => getGCAUserUpdateHistory(userId),
    {
      enabled: !!userId,
      refetchOnMount: true,
    },
  );

  const updateHistory = updateHistoryResponse?.data?.data || [];

  // Extract GCA profile from response and map database fields to component fields
  const getGCATypeKey = (typeValue: string): string => {
    const typeMap: Record<string, string> = {
      TYPE_1: "GOVERNMENT_INSTITUTIONS",
      TYPE_2: "PSU",
      TYPE_3: "NGO",
      TYPE_4: "PRIVATE_COMPANY",
      TYPE_5: "PUBLIC_LIMITED_COMPANY",
      TYPE_6: "PHILANTHROPY",
      TYPE_7: "INDIVIDUAL",
      TYPE_8: "REGISTERED_GROUP",
    };
    return typeMap[typeValue] || typeValue;
  };

  // Transform old user data to match GCA structure based on selected type
  const createGCADataFromOldUser = (
    userData: any,
    gcaType: string,
  ): Partial<GCAData> => {
    const baseData = {
      gcaType,
      status: "Pending",
      documents: userData.documents,
    };

    switch (gcaType) {
      case "GOVERNMENT_INSTITUTIONS":
        return {
          ...baseData,
          nameOfInstitution: userData.firstName || "",
          institutionalEmail: userData.email || "",
          officialContactNumber: userData.phoneNumber || "",
          repFullName:
            userData.nameOfAuthorizedPerson || userData.firstName || "",
          repDesignation: userData.designation || "",
          repEmail: userData.email || "",
          repMobile: {
            countryCode: userData.countryCode || "+91",
            number: userData.phoneNumber || "",
          },
          address: userData.address?.addressLine1 || "",
          state: userData.address?.state || "",
          district: userData.address?.district || "",
          pincode: userData.address?.zipCode || "",
        };

      case "PSU":
        return {
          ...baseData,
          nameOfPSU: userData.firstName || "",
          primaryEmail: userData.email || "",
          primaryContactNumber: userData.phoneNumber || "",
          repFullName:
            userData.nameOfAuthorizedPerson || userData.firstName || "",
          repDesignation: userData.designation || "",
          repEmail: userData.email || "",
          repMobile: {
            countryCode: userData.countryCode || "+91",
            number: userData.phoneNumber || "",
          },
          address: userData.address?.addressLine1 || "",
          state: userData.address?.state || "",
          district: userData.address?.district || "",
          pincode: userData.address?.zipCode || "",
        };

      case "NGO":
        return {
          ...baseData,
          nameOfNGO: userData.firstName || "",
          ngoEmail: userData.email || "",
          ngoContactNumber: userData.phoneNumber || "",
          repFullName:
            userData.nameOfAuthorizedPerson || userData.firstName || "",
          repDesignation: userData.designation || "",
          repEmail: userData.email || "",
          repMobile: {
            countryCode: userData.countryCode || "+91",
            number: userData.phoneNumber || "",
          },
          address: userData.address?.addressLine1 || "",
          state: userData.address?.state || "",
          district: userData.address?.district || "",
          pincode: userData.address?.zipCode || "",
        };

      case "PRIVATE_COMPANY":
        return {
          ...baseData,
          companyName: userData.firstName || "",
          companyEmail: userData.email || "",
          companyMobileNumber: userData.phoneNumber || "",
          repFullName:
            userData.nameOfAuthorizedPerson || userData.firstName || "",
          repDesignation: userData.designation || "",
          repEmail: userData.email || "",
          repMobile: {
            countryCode: userData.countryCode || "+91",
            number: userData.phoneNumber || "",
          },
          address: userData.address?.addressLine1 || "",
          state: userData.address?.state || "",
          district: userData.address?.district || "",
          pincode: userData.address?.zipCode || "",
        };

      case "PUBLIC_LIMITED_COMPANY":
        return {
          ...baseData,
          registeredCompanyName: userData.firstName || "",
          corporateOfficeEmail: userData.email || "",
          repFullName:
            userData.nameOfAuthorizedPerson || userData.firstName || "",
          repDesignation: userData.designation || "",
          repEmail: userData.email || "",
          repMobile: {
            countryCode: userData.countryCode || "+91",
            number: userData.phoneNumber || "",
          },
          address: userData.address?.addressLine1 || "",
          state: userData.address?.state || "",
          district: userData.address?.district || "",
          pincode: userData.address?.zipCode || "",
        };

      case "PHILANTHROPY":
        return {
          ...baseData,
          foundationName: userData.firstName || "",
          foundationEmail: userData.email || "",
          foundationMobile: {
            countryCode: userData.countryCode || "+91",
            number: userData.phoneNumber || "",
          },
          repFullName:
            userData.nameOfAuthorizedPerson || userData.firstName || "",
          repDesignation: userData.designation || "",
          repEmail: userData.email || "",
          repMobile: {
            countryCode: userData.countryCode || "+91",
            number: userData.phoneNumber || "",
          },
          address: userData.address?.addressLine1 || "",
          state: userData.address?.state || "",
          district: userData.address?.district || "",
          pincode: userData.address?.zipCode || "",
        };

      case "INDIVIDUAL":
        return {
          ...baseData,
          individualFullName: userData.firstName || "",
          individualEmail: userData.email || "",
          individualMobile: {
            countryCode: userData.countryCode || "+91",
            number: userData.phoneNumber || "",
          },
          individualOccupation: userData.designation || "",
          address: userData.address?.addressLine1 || "",
          state: userData.address?.state || "",
          district: userData.address?.district || "",
          pincode: userData.address?.zipCode || "",
        };

      case "REGISTERED_GROUP":
        return {
          ...baseData,
          groupName: userData.firstName || "",
          groupEmail: userData.email || "",
          groupContactNumber: {
            countryCode: userData.countryCode || "+91",
            number: userData.phoneNumber || "",
          },
          groupRepresentativeFullName:
            userData.nameOfAuthorizedPerson || userData.firstName || "",
          groupRepresentativeDesignation: userData.designation || "",
          groupRepresentativeEmail: userData.email || "",
          groupRepresentativePhone: userData.phoneNumber || "",
          groupOfficeAddress: userData.address?.addressLine1 || "",
          groupOfficeState: userData.address?.state || "",
          groupOfficeDistrict: userData.address?.district || "",
          groupOfficePincode: userData.address?.zipCode || "",
        };

      default:
        return baseData;
    }
  };

  const mapGCAData = (apiData: any): GCAData | null => {
    if (!apiData) return null;

    const gcaProfile = apiData.gcaProfile?.[0];

    // If no GCA profile exists, this is an old user - create pre-filled form data
    if (!gcaProfile) {
      // Map entityType UUID to GCA type
      const entityTypeMapping: Record<string, string> = {
        "0d984b49-b70c-449c-b493-505ffa32c115": "GOVERNMENT_INSTITUTIONS",
        "2349ac97-480d-446d-a01b-5fc02d6617cf": "PSU",
        "3cf19c7f-7c90-4d81-b297-43bc8cbfa9da": "NGO",
        "4dc93647-093e-4bc7-a390-3f935b100ff3": "PRIVATE_COMPANY",
        "e2215882-38bd-4bc6-a24e-569a40e54a19": "PUBLIC_LIMITED_COMPANY",
        "9bded480-de7b-42d5-96fd-be91b09c2da5": "PHILANTHROPY",
        "adba6d88-c90d-4551-b041-4669530f0c38": "INDIVIDUAL",
        "cfba641c-9026-4cd3-b0b4-5cde09bf1def": "REGISTERED_GROUP",
      };

      // Get GCA type from entityType UUID
      const gcaType =
        entityTypeMapping[apiData.entityType] || "GOVERNMENT_INSTITUTIONS";

      // Return pre-filled GCA data for old users
      const prefilledData = createGCADataFromOldUser(apiData, gcaType);
      return prefilledData as GCAData;
    }

    return {
      id: gcaProfile.id,
      gcaType: getGCATypeKey(gcaProfile.gca_type),
      status: gcaProfile.gca_status,
      createdAt: gcaProfile.created_at,
      updatedAt: gcaProfile.updated_at,

      // Government Institutions
      nameOfInstitution: gcaProfile.entity_name,
      typeOfInstitution: gcaProfile.govt_institution_type,
      ministry: gcaProfile.ministry_department,
      organizationPAN: gcaProfile.govt_institution_pan,
      organizationGST: gcaProfile.govt_institution_gst,
      institutionalEmail: gcaProfile.govt_institution_email,
      officialContactNumber: gcaProfile.govt_institution_contact_number,
      website: gcaProfile.website,
      address: apiData.address?.addressLine1,
      state: apiData.address?.state,
      district: apiData.address?.district,
      pincode: apiData.address?.zipCode,
      repFullName: gcaProfile.representative_full_name,
      repDesignation: gcaProfile.representative_designation,
      repEmail: gcaProfile.representative_email,
      repMobile: gcaProfile.representative_phone
        ? { countryCode: "+91", number: gcaProfile.representative_phone }
        : undefined,

      repEmpId: gcaProfile.representative_employee_id,

      // PSU
      nameOfPSU: gcaProfile.psu_name,
      ownershipType: gcaProfile.psu_sector,
      psuParentCompanyDetails: gcaProfile.psu_parent_details,
      psuSubsidiaryDetails: gcaProfile.psu_subsidiaries_details,
      cinNumber: gcaProfile.psu_cin_gstin,
      gstNumber: gcaProfile.psu_gst_number,
      primaryEmail: gcaProfile.psu_email,
      primaryContactNumber: gcaProfile.psu_contact_number,
      officeAddress: gcaProfile.psu_headquarters,

      // NGO
      nameOfNGO: gcaProfile.ngo_name,
      registrationType: gcaProfile.ngo_registration_type,
      ngoRegistrationNumber: gcaProfile.ngo_registration_number,
      ngoGSTNumber: gcaProfile.ngo_gst_number,
      ngoPANNumber: gcaProfile.ngo_pan_number,
      ngoDarpanID: gcaProfile.ngo_darpan_id,
      ngoEmail: gcaProfile.ngo_email,
      ngoContactNumber: gcaProfile.ngo_contact_number,
      ngoNatureOfWork: gcaProfile.ngo_nature_of_work,

      // Private Company
      companyName: gcaProfile.company_name,
      companyPAN: gcaProfile.company_pan,
      companyGSTNumber: gcaProfile.company_gst_number,
      companyEmail: gcaProfile.company_email,
      companyMobileNumber: gcaProfile.company_contact_number,
      companyParentDetails: gcaProfile.company_parent_details,
      companySubsidiariesDetails: gcaProfile.company_subsidiaries_details,
      companyNatureOfBusiness: gcaProfile.company_nature_of_business,
      companyStatesOfOperation: gcaProfile.company_states_of_operation,
      companyIncorporationDate: gcaProfile.company_incorporation_date,

      // Public Limited Company
      registeredCompanyName: gcaProfile.plc_registered_company_name,
      plcCINNumber: gcaProfile.plc_cin_number,
      plcPANNumber: gcaProfile.plc_pan_number,
      plcGSTNumber: gcaProfile.plc_gst_number,
      sebiListingStatus: gcaProfile.plc_sebi_listing_status,
      corporateOfficeEmail: gcaProfile.plc_email,
      plcParentCompanyDetails: gcaProfile.plc_parent_company_details,
      plcSubsidiariesDetails: gcaProfile.plc_subsidiaries_details,

      // Philanthropies/Foundations
      foundationName: gcaProfile.foundation_name,
      foundationRegistrationNumber: gcaProfile.foundation_registration_number,
      foundationPANNumber: gcaProfile.foundation_pan_number,
      foundationEmail: gcaProfile.foundation_email,
      foundationMobile: gcaProfile.foundation_contact_number
        ? { countryCode: "+91", number: gcaProfile.foundation_contact_number }
        : undefined,
      foundationLegalStatus: gcaProfile.foundation_legal_status,
      foundationFundingSource: gcaProfile.foundation_funding_source,

      // Individuals
      individualFullName:
        gcaProfile.individual_full_name ||
        `${gcaProfile.individual_fname || ""} ${
          gcaProfile.individual_lname || ""
        }`.trim(),
      individualDateOfBirth:
        gcaProfile.individual_dob || gcaProfile.individual_date_of_birth,
      individualGender: gcaProfile.individual_gender,
      individualEmail: apiData.email,
      individualMobile: {
        countryCode: apiData.countryCode,
        number: apiData.phoneNumber,
      },
      individualAadhaarNumber:
        gcaProfile.individual_aadhaar_number || gcaProfile.individual_aadhaar,
      individualPANNumber:
        gcaProfile.individual_pan_number || gcaProfile.individual_pan,
      individualOccupation: gcaProfile.individual_occupation,
      individualOccupationDetails: gcaProfile.individual_occupation_details,

      // Registered Group
      groupName: gcaProfile.group_name,
      groupType: gcaProfile.group_type,
      groupEmail: gcaProfile.group_email,
      groupContactNumber: gcaProfile.group_contact_number
        ? {
            countryCode: "+91",
            number: gcaProfile.group_contact_number,
          }
        : undefined,
      groupRegistrationNumber: gcaProfile.group_registration_number,
      groupOfficeAddress: gcaProfile.group_office_address,
      groupOfficeState: gcaProfile.group_office_state,
      groupOfficeDistrict: gcaProfile.group_office_district,
      groupOfficePincode: gcaProfile.group_office_pincode,
      groupRepresentativeFullName: gcaProfile.representative_full_name,
      groupRepresentativeDesignation: gcaProfile.representative_designation,
      groupRepresentativeEmail: gcaProfile.representative_email,
      groupRepresentativePhone: gcaProfile.representative_phone,
      groupRepresentativeEmployeeId: gcaProfile.representative_employee_id,

      // Documents
      documents: apiData.documents,
    };
  };

  const gcaData: GCAData | null = mapGCAData(gcaResponse?.data?.data) || null;

  const statusId = gcaResponse?.data?.data?.statusId;
  const rawStatus = gcaResponse?.data?.data?.status;
  useEffect(() => {
    if (statusId !== undefined || rawStatus !== undefined) {
      setIsActive(statusId === 5 || rawStatus === "ACTIVE");
    }
  }, [statusId, rawStatus]);

  const totalDocs = gcaData?.documents?.length || 0;
  const allDocsOpened = totalDocs === 0 || openedDocIds.size >= totalDocs;

  const handleDocOpen = (docKey: string | number) => {
    setOpenedDocIds((prev) => {
      if (prev.has(docKey)) return prev;
      const next = new Set(prev);
      next.add(docKey);
      return next;
    });
  };

  // Stored `frontUrl` is a pre-signed S3 URL that expires (~24h). Re-fetch a
  // fresh URL by the S3 key (stored as `serialNo`) and open it in a new tab.
  const openFreshDocument = async (
    key: string | undefined,
    fallbackUrl: string | undefined,
  ) => {
    if (!key && !fallbackUrl) return;
    if (!key) {
      window.open(fallbackUrl, "_blank", "noopener,noreferrer");
      return;
    }
    try {
      const res: any = await getImageById(key);
      const fresh = res?.data?.fileUrl || res?.data?.data?.fileUrl;
      if (fresh) {
        window.open(fresh, "_blank", "noopener,noreferrer");
      } else if (fallbackUrl) {
        window.open(fallbackUrl, "_blank", "noopener,noreferrer");
      }
    } catch (err: any) {
      if (fallbackUrl) {
        window.open(fallbackUrl, "_blank", "noopener,noreferrer");
      } else {
        setShowSnackbar({
          severity: "error",
          message: "Unable to open document. Please try again.",
        });
      }
    }
  };

  const handleViewDocument = (doc: any, docKey: string | number) => {
    handleDocOpen(docKey);
    void openFreshDocument(doc?.serialNo, doc?.frontUrl);
  };

  const handleViewBack = (doc: any, docKey: string | number) => {
    handleDocOpen(docKey);
    void openFreshDocument(doc?.backKey || doc?.serialNo, doc?.backUrl);
  };

  const handleToggleActive = () => {
    if (!userId) return;
    if (isActive) {
      mutateUserStatus({ status: "INACTIVE", userId });
    } else {
      if (!allDocsOpened) return;
      mutateUserStatus({ status: "ACTIVE", userId });
    }
  };

  // Handle edit mode toggle
  const handleEditToggle = () => {
    if (isEditing) {
      setEditData(null);
      setIsEditing(false);
    } else {
      setEditData(JSON.parse(JSON.stringify(gcaData)));
      setIsEditing(true);
    }
  };

  // Handle field change in edit mode
  const handleFieldChange = (field: keyof GCAData, value: any) => {
    if (editData) {
      setEditData({
        ...editData,
        [field]: value,
      });
    }
  };

  // Handle save
  const handleSave = async () => {
    if (!editData || !gcaData) return;

    setIsSaving(true);
    try {
      // Prepare the unified update payload
      const repMobile =
        editData.repMobile ||
        editData.individualMobile ||
        editData.groupContactNumber;
      const repEmail =
        editData.repEmail ||
        editData.individualEmail ||
        editData.ngoEmail ||
        editData.companyEmail ||
        editData.foundationEmail ||
        editData.primaryEmail;

      const updatePayload = {
        userData: {
          firstName:
            editData.nameOfInstitution ||
            editData.nameOfPSU ||
            editData.nameOfNGO ||
            editData.companyName ||
            editData.registeredCompanyName ||
            editData.foundationName ||
            editData.individualFullName ||
            editData.groupName ||
            "",
          email: repEmail || "",
          phoneNumber: repMobile?.number || "",
          countryCode: repMobile?.countryCode || "+91",
          designation: editData.repDesignation || "",
          nameOfAuthorizedPerson:
            editData.repFullName ||
            editData.groupRepresentativeFullName ||
            editData.individualFullName ||
            "",
        },
        addressData: {
          addressLine1:
            editData.address ||
            editData.officeAddress ||
            editData.groupOfficeAddress ||
            "",
          state: editData.state || "",
          district: editData.district || editData.groupOfficeDistrict || "",
          zipCode: editData.pincode || editData.groupOfficePincode || "",
          country: "India",
        },
        gcaProfileData: editData,
      };

      // Call the unified backend update endpoint
      await updateProfileComprehensive(userId, updatePayload);

      setShowSnackbar({
        severity: "success",
        message: "Profile updated successfully",
      });
      setIsEditing(false);
      setEditData(null);
      // Refetch data
      window.location.reload();
    } catch (err: any) {
      setShowSnackbar({
        severity: "error",
        message: err?.message || "Failed to update profile",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const renderGovernmentInstitutions = (
    data: GCAData,
    editMode: boolean = false,
  ) => (
    <>
      <Box {...styles("section")}>
        <Typography {...styles("sectionTitle")}>
          Basic Institution Details
        </Typography>
        <Box {...styles("container")}>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Name of Institution</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.nameOfInstitution || ""}
                onChange={(e) =>
                  handleFieldChange("nameOfInstitution", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.nameOfInstitution || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Type of Institution</Typography>
            {editMode ? (
              <Select
                size="small"
                fullWidth
                value={editData?.typeOfInstitution || ""}
                onChange={(e) =>
                  handleFieldChange("typeOfInstitution", e.target.value)
                }
              >
                <MenuItem value="">Select Type</MenuItem>
                {institutionTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography {...styles("value")}>
                {institutionTypeOptions.find(
                  (o) => o.value === data.typeOfInstitution,
                )?.label ||
                  data.typeOfInstitution ||
                  "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Ministry</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.ministry || ""}
                onChange={(e) => handleFieldChange("ministry", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.ministry || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Organization PAN</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.organizationPAN || ""}
                onChange={(e) =>
                  handleFieldChange("organizationPAN", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.organizationPAN || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Organization GST</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.organizationGST || ""}
                onChange={(e) =>
                  handleFieldChange("organizationGST", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.organizationGST || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Website</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.website || ""}
                onChange={(e) => handleFieldChange("website", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.website || "—"}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      <Box {...styles("section")}>
        <Typography {...styles("sectionTitle")}>Contact Details</Typography>
        <Box {...styles("container")}>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Institutional Email</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                type="email"
                value={editData?.institutionalEmail || ""}
                onChange={(e) =>
                  handleFieldChange("institutionalEmail", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.institutionalEmail || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>
              Official Contact Number
            </Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.officialContactNumber || ""}
                onChange={(e) =>
                  handleFieldChange("officialContactNumber", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.officialContactNumber || "—"}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      <Box {...styles("section")}>
        <Typography {...styles("sectionTitle")}>Registered Address</Typography>
        <Box {...styles("container")}>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Address</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.address || ""}
                onChange={(e) => handleFieldChange("address", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.address || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>State</Typography>
            {editMode ? (
              <Select
                size="small"
                fullWidth
                value={editData?.state || ""}
                onChange={(e) => {
                  handleFieldChange("state", e.target.value);
                  handleFieldChange("district", "");
                }}
              >
                <MenuItem value="">Select state</MenuItem>
                {stateOptions.map((state: any) => (
                  <MenuItem key={state.id} value={state.id}>
                    {state.name}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography {...styles("value")}>
                {stateOptions.find((s: any) => s.id === data.state)?.name ||
                  data.state ||
                  "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>District</Typography>
            {editMode ? (
              <Select
                size="small"
                fullWidth
                value={editData?.district || ""}
                onChange={(e) => handleFieldChange("district", e.target.value)}
                disabled={!editData?.state}
              >
                <MenuItem value="">Select district</MenuItem>
                {districtOptions.map((district: any) => (
                  <MenuItem key={district.id} value={district.id}>
                    {district.name}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography {...styles("value")}>
                {data.state
                  ? stateOptions
                      .find((s: any) => s.id === data.state)
                      ?.districts?.find((d: any) => d.id === data.district)
                      ?.name ||
                    data.district ||
                    "—"
                  : data.district || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Pincode</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.pincode || ""}
                onChange={(e) => handleFieldChange("pincode", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.pincode || "—"}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      <Box {...styles("section")}>
        <Typography {...styles("sectionTitle")}>
          Authorized Representative Details
        </Typography>
        <Box {...styles("container")}>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Full Name</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.repFullName || ""}
                onChange={(e) =>
                  handleFieldChange("repFullName", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repFullName || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Designation</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.repDesignation || ""}
                onChange={(e) =>
                  handleFieldChange("repDesignation", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repDesignation || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Email</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                type="email"
                value={editData?.repEmail || ""}
                onChange={(e) => handleFieldChange("repEmail", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repEmail || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Mobile</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.repMobile?.number || ""}
                onChange={(e) =>
                  handleFieldChange("repMobile", {
                    ...editData?.repMobile,
                    number: e.target.value,
                  })
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repMobile
                  ? `${data.repMobile.countryCode} ${data.repMobile.number}`
                  : "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Employee Id</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.repEmpId || ""}
                onChange={(e) => handleFieldChange("repEmpId", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repEmpId || "—"}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );

  const renderPSU = (data: GCAData, editMode: boolean = false) => (
    <>
      <Box {...styles("section")}>
        <Typography {...styles("sectionTitle")}>
          PSU Organization Details
        </Typography>
        <Box {...styles("container")}>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Name of PSU</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.nameOfPSU || ""}
                onChange={(e) => handleFieldChange("nameOfPSU", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.nameOfPSU || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Ownership Type</Typography>
            {editMode ? (
              <Select
                size="small"
                fullWidth
                value={editData?.ownershipType || ""}
                onChange={(e) =>
                  handleFieldChange("ownershipType", e.target.value)
                }
              >
                <MenuItem value="">Select Ownership</MenuItem>
                {ownershipTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography {...styles("value")}>
                {ownershipTypeOptions.find(
                  (o) => o.value === data.ownershipType,
                )?.label ||
                  data.ownershipType ||
                  "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Parent Company details</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                multiline
                rows={2}
                value={editData?.psuParentCompanyDetails || ""}
                onChange={(e) =>
                  handleFieldChange("psuParentCompanyDetails", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.psuParentCompanyDetails || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Subsidiaries details</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                multiline
                rows={2}
                value={editData?.psuSubsidiaryDetails || ""}
                onChange={(e) =>
                  handleFieldChange("psuSubsidiaryDetails", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.psuSubsidiaryDetails || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>CIN Number</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.cinNumber || ""}
                onChange={(e) => handleFieldChange("cinNumber", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.cinNumber || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>GST Number</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.gstNumber || ""}
                onChange={(e) => handleFieldChange("gstNumber", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.gstNumber || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Primary Email</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                type="email"
                value={editData?.primaryEmail || ""}
                onChange={(e) =>
                  handleFieldChange("primaryEmail", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.primaryEmail || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Primary Contact Number</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.primaryContactNumber || ""}
                onChange={(e) =>
                  handleFieldChange("primaryContactNumber", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.primaryContactNumber || "—"}
              </Typography>
            )}
          </Box>
          {/* <Box {...styles("input")}>
            <Typography {...styles("label")}>Office Address</Typography>
            <Typography {...styles("value")}>
              {data.officeAddress || "—"}
            </Typography>
          </Box> */}
        </Box>
      </Box>
      <Box {...styles("section")}>
        <Typography {...styles("sectionTitle")}>Registered Address</Typography>
        <Box {...styles("container")}>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Address</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.address || ""}
                onChange={(e) => handleFieldChange("address", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.address || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>State</Typography>
            {editMode ? (
              <Select
                size="small"
                fullWidth
                value={editData?.state || ""}
                onChange={(e) => {
                  handleFieldChange("state", e.target.value);
                  handleFieldChange("district", "");
                }}
              >
                <MenuItem value="">Select state</MenuItem>
                {stateOptions.map((state: any) => (
                  <MenuItem key={state.id} value={state.id}>
                    {state.name}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography {...styles("value")}>
                {stateOptions.find((s: any) => s.id === data.state)?.name ||
                  data.state ||
                  "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>District</Typography>
            {editMode ? (
              <Select
                size="small"
                fullWidth
                value={editData?.district || ""}
                onChange={(e) => handleFieldChange("district", e.target.value)}
                disabled={!editData?.state}
              >
                <MenuItem value="">Select district</MenuItem>
                {districtOptions.map((district: any) => (
                  <MenuItem key={district.id} value={district.id}>
                    {district.name}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography {...styles("value")}>
                {data.state
                  ? stateOptions
                      .find((s: any) => s.id === data.state)
                      ?.districts?.find((d: any) => d.id === data.district)
                      ?.name ||
                    data.district ||
                    "—"
                  : data.district || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Pincode</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.pincode || ""}
                onChange={(e) => handleFieldChange("pincode", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.pincode || "—"}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
      <Box {...styles("section")}>
        <Typography {...styles("sectionTitle")}>
          Authorized Representative Details
        </Typography>
        <Box {...styles("container")}>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Full Name</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.repFullName || ""}
                onChange={(e) =>
                  handleFieldChange("repFullName", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repFullName || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Designation</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.repDesignation || ""}
                onChange={(e) =>
                  handleFieldChange("repDesignation", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repDesignation || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Email</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                type="email"
                value={editData?.repEmail || ""}
                onChange={(e) => handleFieldChange("repEmail", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repEmail || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Mobile</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.repMobile?.number || ""}
                onChange={(e) =>
                  handleFieldChange("repMobile", {
                    ...editData?.repMobile,
                    number: e.target.value,
                  })
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repMobile
                  ? `${data.repMobile.countryCode} ${data.repMobile.number}`
                  : "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Employee Id</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.repEmpId || ""}
                onChange={(e) => handleFieldChange("repEmpId", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repEmpId || "—"}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );

  const renderNGO = (data: GCAData, editMode: boolean = false) => (
    <>
      <Box {...styles("section")}>
        <Typography {...styles("sectionTitle")}>NGO Details</Typography>
        <Box {...styles("container")}>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Name of NGO</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.nameOfNGO || ""}
                onChange={(e) => handleFieldChange("nameOfNGO", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.nameOfNGO || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Registration Type</Typography>
            {editMode ? (
              <Select
                size="small"
                fullWidth
                value={editData?.registrationType || ""}
                onChange={(e) =>
                  handleFieldChange("registrationType", e.target.value)
                }
              >
                <MenuItem value="">Select Type</MenuItem>
                {registrationTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography {...styles("value")}>
                {registrationTypeOptions.find(
                  (o) => o.value === data.registrationType,
                )?.label ||
                  data.registrationType ||
                  "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>
              NGO Registration Number
            </Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.ngoRegistrationNumber || ""}
                onChange={(e) =>
                  handleFieldChange("ngoRegistrationNumber", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.ngoRegistrationNumber || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>NGO PAN Number</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.ngoPANNumber || ""}
                onChange={(e) =>
                  handleFieldChange("ngoPANNumber", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.ngoPANNumber || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>NGO GST Number</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.ngoGSTNumber || ""}
                onChange={(e) =>
                  handleFieldChange("ngoGSTNumber", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.ngoGSTNumber || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Darpan ID</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.ngoDarpanID || ""}
                onChange={(e) =>
                  handleFieldChange("ngoDarpanID", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.ngoDarpanID || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Email</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                type="email"
                value={editData?.ngoEmail || ""}
                onChange={(e) => handleFieldChange("ngoEmail", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.ngoEmail || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Contact Number</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.ngoContactNumber || ""}
                onChange={(e) =>
                  handleFieldChange("ngoContactNumber", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.ngoContactNumber || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Nature of Work</Typography>
            {editMode ? (
              <Select
                size="small"
                fullWidth
                value={editData?.ngoNatureOfWork || ""}
                onChange={(e) =>
                  handleFieldChange("ngoNatureOfWork", e.target.value)
                }
              >
                <MenuItem value="">Select Nature</MenuItem>
                {natureOfWorkOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography {...styles("value")}>
                {natureOfWorkOptions.find(
                  (o) => o.value === data.ngoNatureOfWork,
                )?.label ||
                  data.ngoNatureOfWork ||
                  "—"}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
      <Box {...styles("section")}>
        <Typography {...styles("sectionTitle")}>Registered Address</Typography>
        <Box {...styles("container")}>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Address</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.address || ""}
                onChange={(e) => handleFieldChange("address", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.address || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>State</Typography>
            {editMode ? (
              <Select
                size="small"
                fullWidth
                value={editData?.state || ""}
                onChange={(e) => {
                  handleFieldChange("state", e.target.value);
                  handleFieldChange("district", "");
                }}
              >
                <MenuItem value="">Select state</MenuItem>
                {stateOptions.map((state: any) => (
                  <MenuItem key={state.id} value={state.id}>
                    {state.name}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography {...styles("value")}>
                {stateOptions.find((s: any) => s.id === data.state)?.name ||
                  data.state ||
                  "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>District</Typography>
            {editMode ? (
              <Select
                size="small"
                fullWidth
                value={editData?.district || ""}
                onChange={(e) => handleFieldChange("district", e.target.value)}
                disabled={!editData?.state}
              >
                <MenuItem value="">Select district</MenuItem>
                {districtOptions.map((district: any) => (
                  <MenuItem key={district.id} value={district.id}>
                    {district.name}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography {...styles("value")}>
                {data.state
                  ? stateOptions
                      .find((s: any) => s.id === data.state)
                      ?.districts?.find((d: any) => d.id === data.district)
                      ?.name ||
                    data.district ||
                    "—"
                  : data.district || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Pincode</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.pincode || ""}
                onChange={(e) => handleFieldChange("pincode", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.pincode || "—"}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
      <Box {...styles("section")}>
        <Typography {...styles("sectionTitle")}>
          Authorized Representative Details
        </Typography>
        <Box {...styles("container")}>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Full Name</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.repFullName || ""}
                onChange={(e) =>
                  handleFieldChange("repFullName", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repFullName || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Designation</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.repDesignation || ""}
                onChange={(e) =>
                  handleFieldChange("repDesignation", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repDesignation || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Email</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                type="email"
                value={editData?.repEmail || ""}
                onChange={(e) => handleFieldChange("repEmail", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repEmail || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Mobile</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.repMobile?.number || ""}
                onChange={(e) =>
                  handleFieldChange("repMobile", {
                    ...editData?.repMobile,
                    number: e.target.value,
                  })
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repMobile
                  ? `${data.repMobile.countryCode} ${data.repMobile.number}`
                  : "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Employee Id</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.repEmpId || ""}
                onChange={(e) => handleFieldChange("repEmpId", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repEmpId || "—"}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );

  const renderPrivateCompany = (data: GCAData, editMode: boolean = false) => (
    <>
      <Box {...styles("section")}>
        <Typography {...styles("sectionTitle")}>
          Private Company Details
        </Typography>
        <Box {...styles("container")}>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Company Name</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.companyName || ""}
                onChange={(e) =>
                  handleFieldChange("companyName", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.companyName || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Company CIN</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.companyPAN || ""}
                onChange={(e) =>
                  handleFieldChange("companyPAN", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.companyPAN || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Company PAN</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.companyPAN || ""}
                onChange={(e) =>
                  handleFieldChange("companyPAN", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.companyPAN || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Company GST Number</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.companyGSTNumber || ""}
                onChange={(e) =>
                  handleFieldChange("companyGSTNumber", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.companyGSTNumber || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Company Email</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                type="email"
                value={editData?.companyEmail || ""}
                onChange={(e) =>
                  handleFieldChange("companyEmail", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.companyEmail || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Company Mobile Number</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.companyMobileNumber || ""}
                onChange={(e) =>
                  handleFieldChange("companyMobileNumber", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.companyMobileNumber || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Parent Company details</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                multiline
                rows={2}
                value={editData?.companyParentDetails || ""}
                onChange={(e) =>
                  handleFieldChange("companyParentDetails", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.companyParentDetails || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Subsidiaries details</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                multiline
                rows={2}
                value={editData?.companySubsidiariesDetails || ""}
                onChange={(e) =>
                  handleFieldChange(
                    "companySubsidiariesDetails",
                    e.target.value,
                  )
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.companySubsidiariesDetails || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Nature of Business</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.companyNatureOfBusiness || ""}
                onChange={(e) =>
                  handleFieldChange("companyNatureOfBusiness", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.companyNatureOfBusiness || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>States of Operation</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.companyStatesOfOperation || ""}
                onChange={(e) =>
                  handleFieldChange("companyStatesOfOperation", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.companyStatesOfOperation || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Incorporation Date</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                type="date"
                InputLabelProps={{ shrink: true }}
                value={editData?.companyIncorporationDate || ""}
                onChange={(e) =>
                  handleFieldChange("companyIncorporationDate", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.companyIncorporationDate || "—"}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      <Box {...styles("section")}>
        <Typography {...styles("sectionTitle")}>Registered Address</Typography>
        <Box {...styles("container")}>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Address</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.address || ""}
                onChange={(e) => handleFieldChange("address", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.address || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>State</Typography>
            {editMode ? (
              <Select
                size="small"
                fullWidth
                value={editData?.state || ""}
                onChange={(e) => {
                  handleFieldChange("state", e.target.value);
                  handleFieldChange("district", "");
                }}
              >
                <MenuItem value="">Select state</MenuItem>
                {stateOptions.map((state: any) => (
                  <MenuItem key={state.id} value={state.id}>
                    {state.name}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography {...styles("value")}>
                {stateOptions.find((s: any) => s.id === data.state)?.name ||
                  data.state ||
                  "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>District</Typography>
            {editMode ? (
              <Select
                size="small"
                fullWidth
                value={editData?.district || ""}
                onChange={(e) => handleFieldChange("district", e.target.value)}
                disabled={!editData?.state}
              >
                <MenuItem value="">Select district</MenuItem>
                {districtOptions.map((district: any) => (
                  <MenuItem key={district.id} value={district.id}>
                    {district.name}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography {...styles("value")}>
                {data.state
                  ? stateOptions
                      .find((s: any) => s.id === data.state)
                      ?.districts?.find((d: any) => d.id === data.district)
                      ?.name ||
                    data.district ||
                    "—"
                  : data.district || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Pincode</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.pincode || ""}
                onChange={(e) => handleFieldChange("pincode", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.pincode || "—"}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      <Box {...styles("section")}>
        <Typography {...styles("sectionTitle")}>
          Authorized Representative Details
        </Typography>
        <Box {...styles("container")}>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Full Name</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.repFullName || ""}
                onChange={(e) =>
                  handleFieldChange("repFullName", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repFullName || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Designation</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.repDesignation || ""}
                onChange={(e) =>
                  handleFieldChange("repDesignation", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repDesignation || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Email</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                type="email"
                value={editData?.repEmail || ""}
                onChange={(e) => handleFieldChange("repEmail", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repEmail || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Mobile</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.repMobile?.number || ""}
                onChange={(e) =>
                  handleFieldChange("repMobile", {
                    ...editData?.repMobile,
                    number: e.target.value,
                  })
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repMobile
                  ? `${data.repMobile.countryCode} ${data.repMobile.number}`
                  : "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Employee Id</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.repEmpId || ""}
                onChange={(e) => handleFieldChange("repEmpId", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repEmpId || "—"}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );

  const renderPublicLimitedCompany = (
    data: GCAData,
    editMode: boolean = false,
  ) => (
    <>
      <Box {...styles("section")}>
        <Typography {...styles("sectionTitle")}>
          Public Limited Company Details
        </Typography>
        <Box {...styles("container")}>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>
              Registered Company Name
            </Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.registeredCompanyName || ""}
                onChange={(e) =>
                  handleFieldChange("registeredCompanyName", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.registeredCompanyName || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>CIN Number</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.plcCINNumber || ""}
                onChange={(e) =>
                  handleFieldChange("plcCINNumber", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.plcCINNumber || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>PAN Number</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.plcPANNumber || ""}
                onChange={(e) =>
                  handleFieldChange("plcPANNumber", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.plcPANNumber || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>GST Number</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.plcGSTNumber || ""}
                onChange={(e) =>
                  handleFieldChange("plcGSTNumber", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.plcGSTNumber || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>SEBI Listing Status</Typography>
            {editMode ? (
              <Select
                size="small"
                fullWidth
                value={editData?.sebiListingStatus || ""}
                onChange={(e) =>
                  handleFieldChange("sebiListingStatus", e.target.value)
                }
              >
                <MenuItem value="">Select Status</MenuItem>
                {sebiListingStatusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography {...styles("value")}>
                {sebiListingStatusOptions.find(
                  (o) => o.value === data.sebiListingStatus,
                )?.label ||
                  data.sebiListingStatus ||
                  "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Corporate Office Email</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                type="email"
                value={editData?.corporateOfficeEmail || ""}
                onChange={(e) =>
                  handleFieldChange("corporateOfficeEmail", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.corporateOfficeEmail || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Parent Company details</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                multiline
                rows={2}
                value={editData?.plcParentCompanyDetails || ""}
                onChange={(e) =>
                  handleFieldChange("plcParentCompanyDetails", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.plcParentCompanyDetails || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Subsidiaries details</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                multiline
                rows={2}
                value={editData?.plcSubsidiariesDetails || ""}
                onChange={(e) =>
                  handleFieldChange("plcSubsidiariesDetails", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.plcSubsidiariesDetails || "—"}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
      <Box {...styles("section")}>
        <Typography {...styles("sectionTitle")}>Registered Address</Typography>
        <Box {...styles("container")}>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Address</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.address || ""}
                onChange={(e) => handleFieldChange("address", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.address || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>State</Typography>
            {editMode ? (
              <Select
                size="small"
                fullWidth
                value={editData?.state || ""}
                onChange={(e) => {
                  handleFieldChange("state", e.target.value);
                  handleFieldChange("district", "");
                }}
              >
                <MenuItem value="">Select state</MenuItem>
                {stateOptions.map((state: any) => (
                  <MenuItem key={state.id} value={state.id}>
                    {state.name}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography {...styles("value")}>
                {stateOptions.find((s: any) => s.id === data.state)?.name ||
                  data.state ||
                  "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>District</Typography>
            {editMode ? (
              <Select
                size="small"
                fullWidth
                value={editData?.district || ""}
                onChange={(e) => handleFieldChange("district", e.target.value)}
                disabled={!editData?.state}
              >
                <MenuItem value="">Select district</MenuItem>
                {districtOptions.map((district: any) => (
                  <MenuItem key={district.id} value={district.id}>
                    {district.name}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography {...styles("value")}>
                {data.state
                  ? stateOptions
                      .find((s: any) => s.id === data.state)
                      ?.districts?.find((d: any) => d.id === data.district)
                      ?.name ||
                    data.district ||
                    "—"
                  : data.district || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Pincode</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.pincode || ""}
                onChange={(e) => handleFieldChange("pincode", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.pincode || "—"}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
      <Box {...styles("section")}>
        <Typography {...styles("sectionTitle")}>
          Authorized Representative Details
        </Typography>
        <Box {...styles("container")}>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Full Name</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.repFullName || ""}
                onChange={(e) =>
                  handleFieldChange("repFullName", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repFullName || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Designation</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.repDesignation || ""}
                onChange={(e) =>
                  handleFieldChange("repDesignation", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repDesignation || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Email</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                type="email"
                value={editData?.repEmail || ""}
                onChange={(e) => handleFieldChange("repEmail", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repEmail || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Mobile</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.repMobile?.number || ""}
                onChange={(e) =>
                  handleFieldChange("repMobile", {
                    ...editData?.repMobile,
                    number: e.target.value,
                  })
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repMobile
                  ? `${data.repMobile.countryCode} ${data.repMobile.number}`
                  : "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Employee Id</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.repEmpId || ""}
                onChange={(e) => handleFieldChange("repEmpId", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repEmpId || "—"}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );

  const renderPhilanthropy = (data: GCAData, editMode: boolean = false) => (
    <>
      <Box {...styles("section")}>
        <Typography {...styles("sectionTitle")}>
          Philanthropy/Foundation Details
        </Typography>
        <Box {...styles("container")}>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Foundation Name</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.foundationName || ""}
                onChange={(e) =>
                  handleFieldChange("foundationName", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.foundationName || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Legal Status</Typography>
            {editMode ? (
              <Select
                size="small"
                fullWidth
                value={editData?.foundationLegalStatus || ""}
                onChange={(e) =>
                  handleFieldChange("foundationLegalStatus", e.target.value)
                }
              >
                <MenuItem value="">Select Status</MenuItem>
                {legalStatusFoundationOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography {...styles("value")}>
                {legalStatusFoundationOptions.find(
                  (o) => o.value === data.foundationLegalStatus,
                )?.label ||
                  data.foundationLegalStatus ||
                  "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Registration Number</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.foundationRegistrationNumber || ""}
                onChange={(e) =>
                  handleFieldChange(
                    "foundationRegistrationNumber",
                    e.target.value,
                  )
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.foundationRegistrationNumber || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>PAN Number</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.foundationPANNumber || ""}
                onChange={(e) =>
                  handleFieldChange("foundationPANNumber", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.foundationPANNumber || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Funding Source</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.foundationFundingSource || ""}
                onChange={(e) =>
                  handleFieldChange("foundationFundingSource", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.foundationFundingSource || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Email</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                type="email"
                value={editData?.foundationEmail || ""}
                onChange={(e) =>
                  handleFieldChange("foundationEmail", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.foundationEmail || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Mobile</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.foundationMobile?.number || ""}
                onChange={(e) =>
                  handleFieldChange("foundationMobile", {
                    ...editData?.foundationMobile,
                    number: e.target.value,
                  })
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.foundationMobile
                  ? `${data.foundationMobile.countryCode} ${data.foundationMobile.number}`
                  : "—"}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
      <Box {...styles("section")}>
        <Typography {...styles("sectionTitle")}>Registered Address</Typography>
        <Box {...styles("container")}>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Address</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.address || ""}
                onChange={(e) => handleFieldChange("address", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.address || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>State</Typography>
            {editMode ? (
              <Select
                size="small"
                fullWidth
                value={editData?.state || ""}
                onChange={(e) => {
                  handleFieldChange("state", e.target.value);
                  handleFieldChange("district", "");
                }}
              >
                <MenuItem value="">Select state</MenuItem>
                {stateOptions.map((state: any) => (
                  <MenuItem key={state.id} value={state.id}>
                    {state.name}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography {...styles("value")}>
                {stateOptions.find((s: any) => s.id === data.state)?.name ||
                  data.state ||
                  "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>District</Typography>
            {editMode ? (
              <Select
                size="small"
                fullWidth
                value={editData?.district || ""}
                onChange={(e) => handleFieldChange("district", e.target.value)}
                disabled={!editData?.state}
              >
                <MenuItem value="">Select district</MenuItem>
                {districtOptions.map((district: any) => (
                  <MenuItem key={district.id} value={district.id}>
                    {district.name}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography {...styles("value")}>
                {data.state
                  ? stateOptions
                      .find((s: any) => s.id === data.state)
                      ?.districts?.find((d: any) => d.id === data.district)
                      ?.name ||
                    data.district ||
                    "—"
                  : data.district || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Pincode</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.pincode || ""}
                onChange={(e) => handleFieldChange("pincode", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.pincode || "—"}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
      <Box {...styles("section")}>
        <Typography {...styles("sectionTitle")}>
          Authorized Representative Details
        </Typography>
        <Box {...styles("container")}>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Full Name</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.repFullName || ""}
                onChange={(e) =>
                  handleFieldChange("repFullName", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repFullName || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Designation</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.repDesignation || ""}
                onChange={(e) =>
                  handleFieldChange("repDesignation", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repDesignation || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Email</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                type="email"
                value={editData?.repEmail || ""}
                onChange={(e) => handleFieldChange("repEmail", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repEmail || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Mobile</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.repMobile?.number || ""}
                onChange={(e) =>
                  handleFieldChange("repMobile", {
                    ...editData?.repMobile,
                    number: e.target.value,
                  })
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repMobile
                  ? `${data.repMobile.countryCode} ${data.repMobile.number}`
                  : "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Employee Id</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.repEmpId || ""}
                onChange={(e) => handleFieldChange("repEmpId", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repEmpId || "—"}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );

  const renderIndividual = (data: GCAData, editMode: boolean = false) => (
    <>
      <Box {...styles("section")}>
        <Typography {...styles("sectionTitle")}>Personal Details</Typography>
        <Box {...styles("container")}>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Full Name</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.individualFullName || ""}
                onChange={(e) =>
                  handleFieldChange("individualFullName", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.individualFullName || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Date of Birth</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                type="date"
                InputLabelProps={{ shrink: true }}
                value={editData?.individualDateOfBirth?.slice(0, 10) || ""}
                onChange={(e) =>
                  handleFieldChange("individualDateOfBirth", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.individualDateOfBirth?.slice(0, 10) || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Gender</Typography>
            {editMode ? (
              <Select
                size="small"
                fullWidth
                value={editData?.individualGender || ""}
                onChange={(e) =>
                  handleFieldChange("individualGender", e.target.value)
                }
              >
                <MenuItem value="">Select Gender</MenuItem>
                {genderOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography {...styles("value")}>
                {genderOptions.find((o) => o.value === data.individualGender)
                  ?.label ||
                  data.individualGender ||
                  "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Aadhaar Number</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.individualAadhaarNumber || ""}
                onChange={(e) =>
                  handleFieldChange("individualAadhaarNumber", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.individualAadhaarNumber || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>PAN Number</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.individualPANNumber || ""}
                onChange={(e) =>
                  handleFieldChange("individualPANNumber", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.individualPANNumber || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Email</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                type="email"
                value={editData?.individualEmail || ""}
                onChange={(e) =>
                  handleFieldChange("individualEmail", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.individualEmail || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Mobile</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.individualMobile?.number || ""}
                onChange={(e) =>
                  handleFieldChange("individualMobile", {
                    ...editData?.individualMobile,
                    number: e.target.value,
                  })
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.individualMobile
                  ? `${data.individualMobile.countryCode} ${data.individualMobile.number}`
                  : "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Occupation</Typography>
            {editMode ? (
              <Select
                size="small"
                fullWidth
                value={editData?.individualOccupation || ""}
                onChange={(e) =>
                  handleFieldChange("individualOccupation", e.target.value)
                }
              >
                <MenuItem value="">Select Occupation</MenuItem>
                {occupationDetailsOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography {...styles("value")}>
                {occupationDetailsOptions.find(
                  (o) => o.value === data.individualOccupation,
                )?.label ||
                  data.individualOccupation ||
                  "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Occupation Details</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.individualOccupationDetails || ""}
                onChange={(e) =>
                  handleFieldChange(
                    "individualOccupationDetails",
                    e.target.value,
                  )
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.individualOccupationDetails || "—"}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
      <Box {...styles("section")}>
        <Typography {...styles("sectionTitle")}>Registered Address</Typography>
        <Box {...styles("container")}>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Address</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.address || ""}
                onChange={(e) => handleFieldChange("address", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.address || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>State</Typography>
            {editMode ? (
              <Select
                size="small"
                fullWidth
                value={editData?.state || ""}
                onChange={(e) => {
                  handleFieldChange("state", e.target.value);
                  handleFieldChange("district", "");
                }}
              >
                <MenuItem value="">Select state</MenuItem>
                {stateOptions.map((state: any) => (
                  <MenuItem key={state.id} value={state.id}>
                    {state.name}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography {...styles("value")}>
                {stateOptions.find((s: any) => s.id === data.state)?.name ||
                  data.state ||
                  "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>District</Typography>
            {editMode ? (
              <Select
                size="small"
                fullWidth
                value={editData?.district || ""}
                onChange={(e) => handleFieldChange("district", e.target.value)}
                disabled={!editData?.state}
              >
                <MenuItem value="">Select district</MenuItem>
                {districtOptions.map((district: any) => (
                  <MenuItem key={district.id} value={district.id}>
                    {district.name}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography {...styles("value")}>
                {data.state
                  ? stateOptions
                      .find((s: any) => s.id === data.state)
                      ?.districts?.find((d: any) => d.id === data.district)
                      ?.name ||
                    data.district ||
                    "—"
                  : data.district || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Pincode</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.pincode || ""}
                onChange={(e) => handleFieldChange("pincode", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.pincode || "—"}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );

  const renderRegisteredGroup = (data: GCAData, editMode: boolean = false) => (
    <>
      <Box {...styles("section")}>
        <Typography {...styles("sectionTitle")}>Group Details</Typography>
        <Box {...styles("container")}>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Group Name</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.groupName || ""}
                onChange={(e) => handleFieldChange("groupName", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.groupName || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Group Type</Typography>
            {editMode ? (
              <Select
                size="small"
                fullWidth
                value={editData?.groupType || ""}
                onChange={(e) => handleFieldChange("groupType", e.target.value)}
              >
                <MenuItem value="">Select Group Type</MenuItem>
                {groupTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography {...styles("value")}>
                {groupTypeOptions.find((o) => o.value === data.groupType)
                  ?.label ||
                  data.groupType ||
                  "—"}
              </Typography>
            )}
          </Box>

          <Box {...styles("input")}>
            <Typography {...styles("label")}>Registration Number</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.groupRegistrationNumber || ""}
                onChange={(e) =>
                  handleFieldChange("groupRegistrationNumber", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.groupRegistrationNumber || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Email</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                type="email"
                value={editData?.groupEmail || ""}
                onChange={(e) =>
                  handleFieldChange("groupEmail", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.groupEmail || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Contact Number</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.groupContactNumber?.number || ""}
                onChange={(e) =>
                  handleFieldChange("groupContactNumber", {
                    ...editData?.groupContactNumber,
                    number: e.target.value,
                  })
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.groupContactNumber
                  ? `${data.groupContactNumber.countryCode} ${data.groupContactNumber.number}`
                  : "—"}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      <Box {...styles("section")}>
        <Typography {...styles("sectionTitle")}>
          Office Address Details
        </Typography>
        <Box {...styles("container")}>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Address</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.address || ""}
                onChange={(e) => handleFieldChange("address", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.address || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>State</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.state || ""}
                onChange={(e) => handleFieldChange("state", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>{data.state || "—"}</Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>District</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.district || ""}
                onChange={(e) => handleFieldChange("district", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.district || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Pincode</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.pincode || ""}
                onChange={(e) => handleFieldChange("pincode", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.pincode || "—"}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      <Box {...styles("section")}>
        <Typography {...styles("sectionTitle")}>
          Authorized Representative Details
        </Typography>
        <Box {...styles("container")}>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Full Name</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.groupRepresentativeFullName || ""}
                onChange={(e) =>
                  handleFieldChange(
                    "groupRepresentativeFullName",
                    e.target.value,
                  )
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.groupRepresentativeFullName || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Designation</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.groupRepresentativeDesignation || ""}
                onChange={(e) =>
                  handleFieldChange(
                    "groupRepresentativeDesignation",
                    e.target.value,
                  )
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.groupRepresentativeDesignation || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Email</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                type="email"
                value={editData?.groupRepresentativeEmail || ""}
                onChange={(e) =>
                  handleFieldChange("groupRepresentativeEmail", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.groupRepresentativeEmail || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Phone</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.groupRepresentativePhone || ""}
                onChange={(e) =>
                  handleFieldChange("groupRepresentativePhone", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.groupRepresentativePhone || "—"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Employee ID</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.groupRepresentativeEmployeeId || ""}
                onChange={(e) =>
                  handleFieldChange(
                    "groupRepresentativeEmployeeId",
                    e.target.value,
                  )
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.groupRepresentativeEmployeeId || "—"}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );

  const renderGCATypeContent = (data: GCAData, editMode: boolean = false) => {
    switch (data.gcaType) {
      case "GOVERNMENT_INSTITUTIONS":
        return renderGovernmentInstitutions(data, editMode);
      case "PSU":
        return renderPSU(data, editMode);
      case "NGO":
        return renderNGO(data, editMode);
      case "PRIVATE_COMPANY":
        return renderPrivateCompany(data, editMode);
      case "PUBLIC_LIMITED_COMPANY":
        return renderPublicLimitedCompany(data, editMode);
      case "PHILANTHROPY":
        return renderPhilanthropy(data, editMode);
      case "INDIVIDUAL":
        return renderIndividual(data, editMode);
      case "REGISTERED_GROUP":
        return renderRegisteredGroup(data, editMode);
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <DashboardWrapper title="GCA View" showBackBtn={true} backPath="/users">
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      </DashboardWrapper>
    );
  }

  if (isError || !gcaData) {
    return (
      <DashboardWrapper title="GCA View" showBackBtn={true} backPath="/users">
        <Box {...styles("wrapper")}>
          <Typography color="error">
            Failed to load GCA details. Please try again.
          </Typography>
        </Box>
      </DashboardWrapper>
    );
  }

  return (
    <DashboardWrapper
      title="GCA Profile"
      showBackBtn={true}
      backPath="/users"
      customStyles={{
        children: {
          backgroundColor: "#f4f6f8",
          padding: "32px 24px",
        },
      }}
    >
      <Box {...styles("wrapper")}>
        {/* Header Section */}
        <Box {...styles("childWrapper")}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            gap="20px"
            flexWrap="wrap"
          >
            <div>
              <Typography {...styles("heading")}>
                {GCATypeLabels[gcaData.gcaType] || gcaData.gcaType}
              </Typography>
              <Box display="flex" gap="10px" marginTop="10px">
                <Chip
                  label={GCATypeLabels[gcaData.gcaType]}
                  color="primary"
                  size="small"
                />
                <Chip
                  label={isActive ? "Active" : "Inactive"}
                  size="small"
                  sx={{
                    backgroundColor: isActive ? "#4caf50" : "#9e9e9e",
                    color: "white",
                  }}
                />
              </Box>
            </div>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Badge
                color="error"
                badgeContent={queriesUnreadCount}
                max={99}
                overlap="rectangular"
              >
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => setQueriesOpen(true)}
                  disabled={!userId}
                >
                  Queries
                </Button>
              </Badge>
            </Box>
            {/* <Box {...styles("actionButtons")}>
              {!isEditing ? (
                <>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleEditToggle}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => navigate("/users")}
                  >
                    Back to Users
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    {isSaving ? "Saving..." : "Save Changes"}
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleEditToggle}
                    disabled={isSaving}
                  >
                    Cancel
                  </Button>
                </>
              )}
            </Box> */}
          </Box>
        </Box>

        {/* GCA Data Section */}
        <Box {...styles("childWrapper")}>
          {renderGCATypeContent(gcaData, isEditing)}
        </Box>
      </Box>
      <Box {...styles("childWrapper")}>
        {/* Documents Grid */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Uploaded Documents
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
              gap: 2,
            }}
          >
            {gcaData?.documents && gcaData.documents.length > 0 ? (
              gcaData.documents.map((doc: any, idx: number) => {
                const docKey = doc.id || idx;
                const isOpened = openedDocIds.has(docKey);
                return (
                  <Box
                    key={docKey}
                    sx={{
                      border: isOpened ? "1px solid #4caf50" : "1px solid #ddd",
                      borderRadius: "8px",
                      p: 2,
                      display: "flex",
                      gap: 1.5,
                      alignItems: "flex-start",
                      "&:hover": { boxShadow: 2 },
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          wordBreak: "break-word",
                        }}
                      >
                        {DocumentTypeLabels[doc.type] ||
                          doc.type ||
                          `Document ${idx + 1}`}
                        {isOpened && (
                          <span style={{ color: "#4caf50", marginLeft: 6 }}>
                            ✓
                          </span>
                        )}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          mb: 1,
                          color: "gray",
                          wordBreak: "break-all",
                        }}
                      >
                        {doc.serialNo}
                      </Typography>
                      {doc.frontUrl ? (
                        <>
                          <Box
                            component="span"
                            onClick={() => handleViewDocument(doc, docKey)}
                            sx={{
                              color: "#2196f3",
                              cursor: "pointer",
                              textDecoration: "underline",
                            }}
                          >
                            View
                          </Box>
                          {doc.backUrl && (
                            <>
                              {" | "}
                              <Box
                                component="span"
                                onClick={() => handleViewBack(doc, docKey)}
                                sx={{
                                  color: "#2196f3",
                                  cursor: "pointer",
                                  textDecoration: "underline",
                                }}
                              >
                                View Back
                              </Box>
                            </>
                          )}
                        </>
                      ) : (
                        <Typography variant="caption" sx={{ color: "gray" }}>
                          File URL not available
                        </Typography>
                      )}
                    </Box>
                    {doc.frontUrl && (
                      <Box
                        onClick={() => handleViewDocument(doc, docKey)}
                        sx={{ cursor: "pointer", flexShrink: 0 }}
                      >
                        <DocumentPreview
                          serialNo={doc.serialNo}
                          fallbackUrl={doc.frontUrl}
                        />
                      </Box>
                    )}
                  </Box>
                );
              })
            ) : (
              <Typography
                variant="body2"
                sx={{ color: "gray", gridColumn: "1 / -1" }}
              >
                No documents uploaded yet
              </Typography>
            )}
          </Box>
        </Box>

        {/* Images Grid - Front and Back */}
        {/* <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Document Images
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
                  gap: 2,
                }}
              >
                {Array.isArray(gcaData?.documents) &&
                gcaData.documents.length > 0 ? (
                  gcaData.documents
                    .map((doc: any, idx: number) => {
                      const images: {
                        url: string;
                        label: string;
                        docType: string;
                      }[] = [];
                      if (doc.frontUrl) {
                        images.push({
                          url: doc.frontUrl,
                          label: "Front",
                          docType: doc.type || "Document",
                        });
                      }
                      if (doc.backUrl) {
                        images.push({
                          url: doc.backUrl,
                          label: "Back",
                          docType: doc.type || "Document",
                        });
                      }
                      return images;
                    })
                    .flat()
                    .map((img: any, idx: number) => (
                      <Box
                        key={`${img.docType}-${img.label}-${idx}`}
                        sx={{
                          border: "1px solid #ddd",
                          borderRadius: "8px",
                          p: 1,
                          textAlign: "center",
                          overflow: "hidden",
                        }}
                      >
                        <img
                          src={img.url}
                          alt={`${img.docType} - ${img.label}`}
                          style={{
                            width: "100%",
                            height: "120px",
                            objectFit: "cover",
                            borderRadius: "4px",
                            marginBottom: "8px",
                          }}
                        />
                        <Typography variant="caption" sx={{ display: "block" }}>
                          {img.docType} - {img.label}
                        </Typography>
                      </Box>
                    ))
                ) : (
                  <Typography
                    variant="body2"
                    sx={{ color: "gray", gridColumn: "1 / -1" }}
                  >
                    No images uploaded
                  </Typography>
                )}
              </Box>
            </Box> */}
      </Box>

      {/* Update History Section */}
      <Box {...styles("childWrapper")}>
        <Typography {...styles("sectionTitle")}>Update History</Typography>
        {isHistoryLoading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : updateHistory && updateHistory.length > 0 ? (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell sx={{ fontWeight: 600 }}>Date & Time</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Phone Number</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Designation</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    Authorized Person
                  </TableCell>
                  {/* <TableCell sx={{ fontWeight: 600 }}>Division ID</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Joining Date</TableCell> */}
                </TableRow>
              </TableHead>
              <TableBody>
                {updateHistory.map((history: any, idx: number) => (
                  <TableRow
                    key={history.id || idx}
                    sx={{ "&:hover": { backgroundColor: "#fafafa" } }}
                  >
                    <TableCell>
                      {new Date(history.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell>{history.name || "—"}</TableCell>
                    <TableCell>{history.email || "—"}</TableCell>
                    <TableCell>{history.phoneNumber || "—"}</TableCell>
                    <TableCell>{history.designation || "—"}</TableCell>
                    <TableCell>
                      {history.nameOfAuthorizedPerson || "—"}
                    </TableCell>
                    {/* <TableCell>{history.divisionId || "—"}</TableCell>
                    <TableCell>
                      {history.joiningDate
                        ? new Date(history.joiningDate).toLocaleDateString()
                        : "—"}
                    </TableCell> */}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="body2" sx={{ color: "gray", p: 2 }}>
            No update history available
          </Typography>
        )}
      </Box>

      {isApprover && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            py: 5,
            width: "100%",
            maxWidth: "1200px",
            mx: "auto",
            px: { xs: 3, md: 6 },
            boxSizing: "border-box",
          }}
        >
          {!isActive && totalDocs > 0 && (
            <Typography
              variant="caption"
              sx={{
                color: allDocsOpened ? "success.main" : "warning.main",
                fontWeight: 500,
              }}
            >
              {allDocsOpened
                ? "All documents reviewed. You can now activate the user."
                : `Open all documents to enable activation (${openedDocIds.size}/${totalDocs} reviewed)`}
            </Typography>
          )}
          <Button
            variant="contained"
            color={isActive ? "error" : "success"}
            onClick={handleToggleActive}
            disabled={isStatusUpdating || (!isActive && !allDocsOpened)}
            sx={{ minWidth: 200 }}
          >
            {isStatusUpdating
              ? "Updating..."
              : isActive
              ? "Deactivate User"
              : "Activate User"}
          </Button>
        </Box>
      )}

      {userId && (
        <QueriesDialog
          open={queriesOpen}
          onClose={() => setQueriesOpen(false)}
          gcaUserId={userId}
        />
      )}

      <Snackbar
        open={!!showSnackbar.message}
        message={showSnackbar.message}
        severity={showSnackbar.severity}
        resetSnackbar={() =>
          setShowSnackbar({ severity: "success", message: "" })
        }
      />
    </DashboardWrapper>
  );
};

export default GCAView;
